import { z } from "zod";
import { RARETES, TYPES_EDITION } from "./carte-impression";
import { MODELES } from "./diffusion";

export * from "./carte-impression";

/**
 * Les contrats des routes des cartes du TCG (lot 5, plan `docs/plans/2026-10-31-castellan-tcg-cartes.md`) :
 * pour l'instant la liste des editions de la regie. Aucun identifiant n'est un nombre : un `bigint` de la base
 * voyage en texte. Les dates restent en ISO 8601.
 */

export const STATUTS_EDITION = ["en_circulation", "en_attente", "archivee", "remplacee"] as const;
export const ETATS_CHRONIQUE = ["a_ecrire", "a_valider", "echec", "validee"] as const;
export const ETATS_IMAGE = ["attente", "a_imprimer", "imprimee", "echec"] as const;

export const editionResumeSchema = z.object({
  id: z.string(),
  nom: z.string(),
  viewerId: z.string().nullable(),
  type: z.enum(TYPES_EDITION),
  rarete: z.enum(RARETES),
  numero: z.number().int(),
  numeroAlbum: z.number().int(),
  creeLe: z.string(),
  chroniqueEtat: z.enum(ETATS_CHRONIQUE),
  /** Qui a ecrit le texte de depart (`null` : pas encore de texte). */
  chroniqueSource: z.enum(["ia", "ia_corrigee", "sans_ia", "nicolas"]).nullable(),
  /** Combien de fois l'IA a ete relancee (0 a 3). */
  relances: z.number().int(),
  imageEtat: z.enum(ETATS_IMAGE),
  /** Quand l'image a ete imprimee, `null` avant. */
  imprimeeLe: z.string().nullable(),
  exemplaires: z.number().int(),
  tirageMax: z.number().int().nullable(),
  statut: z.enum(STATUTS_EDITION),
  stats: z.array(z.object({ libelle: z.string(), valeur: z.number().int() })),
});
export type EditionResume = z.infer<typeof editionResumeSchema>;

export const editionsSchema = z.object({
  saison: z.number().int(),
  editions: z.array(editionResumeSchema),
  totaux: z.object({
    cartes: z.number().int(),
    editions: z.number().int(),
    exemplaires: z.number().int(),
    aValider: z.number().int(),
    aImprimer: z.number().int(),
  }),
});
export type Editions = z.infer<typeof editionsSchema>;

/** `GET /api/tcg/editions?type=normale|raid|moment|chatelain` : sans `type`, toutes. */
export const editionsQuerySchema = z.object({
  type: z.enum(TYPES_EDITION).optional(),
});
export type EditionsQuery = z.infer<typeof editionsQuerySchema>;

/** `POST /api/tcg/viewers/{id}/retirer-carte` : l'identifiant en base du viewer, en texte de chiffres. */
export const viewerCarteParamsSchema = z.object({
  id: z.string().regex(/^[0-9]{1,18}$/),
});

/** `GET /api/tcg/editions/{id}/image`, `.../carte` et `.../avatar` : l'identifiant en base de l'edition, en texte de chiffres. */
export const editionParamsSchema = z.object({
  id: z.string().regex(/^[0-9]{1,18}$/),
});

/**
 * La file de validation des chroniques (lot 5, PR 4) : `GET /api/tcg/chroniques`, les corrections, les validations,
 * les relances et les reglages de l'IA des cartes. Rien de ce que le serveur dit n'est une phrase : des codes d'une
 * liste fermee, que la regie met en mots (`FRONT/cartes/cartes-texts.ts`).
 */

/** Les huit controles automatiques d'une chronique, dans l'ordre ou `controler` les rend. */
export const CODES_CONTROLE = [
  "longueur",
  "tirets",
  "adresse",
  "arobase",
  "emoji",
  "citation",
  "vie_privee",
  "moquerie",
] as const;
export type CodeControle = (typeof CODES_CONTROLE)[number];

export const ISSUES_CONTROLE = ["ok", "a_verifier", "bloquant"] as const;

/** Pourquoi une edition existe : sa premiere carte, une montee de rang, un raid, un Moment, le Chatelain. */
export const MOTIFS_EDITION = ["premiere", "montee", "raid", "moment", "chatelain"] as const;
export type MotifEdition = (typeof MOTIFS_EDITION)[number];

/** Qui a ecrit le texte de depart : l'IA, l'IA puis Nicolas, la ligne factuelle, ou Nicolas seul. */
export const SOURCES_CHRONIQUE = ["ia", "ia_corrigee", "sans_ia", "nicolas"] as const;
/** Pourquoi l'IA n'a pas rendu de texte. */
export const ECHECS_CHRONIQUE = ["refus", "invalide", "indisponible"] as const;
/** Le dernier etat de l'ecrivain, tel que la regie le dit. */
export const ETATS_IA_CARTES = [
  "ok",
  "non_configure",
  "plafond",
  "sous_plafond",
  "en_pause",
] as const;
export type EtatIaCartes = (typeof ETATS_IA_CARTES)[number];

/** Une chronique ne depasse pas 140 caracteres : le texte recu en accepte davantage, pour que le controle le dise. */
export const TEXTE_CHRONIQUE_MAX = 400;
/** Combien de fois l'IA peut relancer une edition. */
export const RELANCES_MAX = 3;
/** Les bornes du sous-plafond des cartes, en centimes par mois. */
export const PLAFOND_CARTES_MAX_CENTIMES = 5000;

export const controleSchema = z.object({
  code: z.enum(CODES_CONTROLE),
  issue: z.enum(ISSUES_CONTROLE),
});
export type ControleChronique = z.infer<typeof controleSchema>;

export const elementFileSchema = z.object({
  edition: editionResumeSchema,
  motif: z.enum(MOTIFS_EDITION),
  chronique: z.string().nullable(),
  source: z.enum(SOURCES_CHRONIQUE).nullable(),
  echec: z.enum(ECHECS_CHRONIQUE).nullable(),
  controles: z.array(controleSchema),
  sources: z.object({
    /** La description Twitch lue par l'IA, nettoyee. Vide : l'IA n'en a pas lu. */
    description: z.string().nullable(),
    comptesRendus: z.number().int(),
    /** Les moments forts figes avec l'edition, en phrases courtes de l'app. */
    moments: z.array(z.string()),
  }),
  relancesRestantes: z.number().int(),
  /** `version_figee` : renvoyee a chaque ecriture, pour qu'une edition refigee entre-temps soit refusee. */
  version: z.number().int(),
});
export type ElementFile = z.infer<typeof elementFileSchema>;

export const fileSchema = z.object({
  /** Le numero de la saison ouverte (0 sans saison ouverte). */
  saison: z.number().int(),
  /** A valider et en echec d'abord, puis a ecrire ; 100 au plus. */
  elements: z.array(elementFileSchema),
  /** Combien d'editions attendent l'IA (tout le monde, pas seulement les 100 montrees). */
  enAttenteDEcriture: z.number().int(),
  cout: z.object({
    centimes: z.number().int(),
    plafond: z.number().int(),
    modele: z.enum(MODELES),
  }),
  ia: z.enum(ETATS_IA_CARTES),
});
export type FileChroniques = z.infer<typeof fileSchema>;

/** `PUT /api/tcg/editions/{id}/chronique` et `POST /api/tcg/editions/{id}/valider`. */
export const texteChroniqueCorpsSchema = z.object({
  texte: z.string().min(1).max(TEXTE_CHRONIQUE_MAX),
  version: z.number().int().min(1),
});
export type TexteChroniqueCorps = z.infer<typeof texteChroniqueCorpsSchema>;

/** `POST /api/tcg/editions/{id}/relancer`. */
export const versionCorpsSchema = z.object({ version: z.number().int().min(1) });

export const reglagesCartesSchema = z.object({
  modele: z.enum(MODELES),
  plafondCentimes: z.number().int().min(0).max(PLAFOND_CARTES_MAX_CENTIMES),
});
export type ReglagesCartes = z.infer<typeof reglagesCartesSchema>;

/**
 * La file des signalements de la regie (lot 5, PR 5) : `GET /api/tcg/signalements` et
 * `POST /api/tcg/signalements/{id}/traiter`. Les signalements n'ont aucune trace du signaleur : seulement la carte et
 * le compte par motif.
 */
export const DECISIONS_SIGNALEMENT = ["retirer", "garder"] as const;

export const signalementSchema = z.object({
  id: z.string(),
  editionId: z.string(),
  /** L'adresse de la carte : « 7-3 ». */
  ref: z.string(),
  nom: z.string(),
  rarete: z.enum(RARETES),
  type: z.enum(TYPES_EDITION),
  chroniqueEtat: z.enum(ETATS_CHRONIQUE),
  /** Faux si l'edition n'a pas de viewer (le Chatelain, un Moment) : rien a retirer. */
  retirable: z.boolean(),
  blessante: z.number().int(),
  privee: z.number().int(),
  autre: z.number().int(),
  premierLe: z.string(),
  dernierLe: z.string(),
  traiteLe: z.string().nullable(),
  decision: z.enum(["retiree", "gardee"]).nullable(),
});
export type Signalement = z.infer<typeof signalementSchema>;

export const signalementsSchema = z.object({
  /** Les lignes ouvertes, la plus recente d'abord. */
  ouverts: z.array(signalementSchema),
  /** Les 50 dernieres traitees dont la carte existe encore (retirer une carte efface son signalement). */
  traites: z.array(signalementSchema),
});
export type Signalements = z.infer<typeof signalementsSchema>;

export const signalementParamsSchema = z.object({ id: z.string().regex(/^[0-9]{1,18}$/) });
export const traiterSignalementCorpsSchema = z.object({ decision: z.enum(DECISIONS_SIGNALEMENT) });
export type TraiterSignalementCorps = z.infer<typeof traiterSignalementCorpsSchema>;
